import { Mutation, QueryClient } from '@tanstack/react-query';

/**
 * Tracks which tasks currently have a write in flight, so socket handlers can
 * tell "the server is echoing my own change back" from "a peer changed this".
 *
 * Once the server stops excluding the initiator (REALTIME_EXCLUDE_INITIATOR=false)
 * every mutating tab receives an echo of its own change. Applying echoes is what
 * heals mutation sites that forget to patch the cache, but a late echo carrying
 * pre-commit state would clobber a newer local edit — e.g. TaskView's 1s-debounced
 * description save. Handlers therefore skip an event while a write for that task is
 * pending, and reconcile once it settles (audit §4.3).
 *
 * Discrimination is by mutationKey + variables rather than a parallel registry, so
 * there is nothing to keep in sync: React Query already knows what is in flight.
 * Every task write must run through a keyed mutation or it is invisible here.
 */

/** Single-task writes: `useUpdateTaskMutation`, variables `{ taskId }`. */
export const TASK_MUTATION_KEY = ['task'] as const;
/** Batch writes: `useUpdateTasksMutation` / `useDeleteTasksMutation`, variables `{ taskIds }`. */
export const TASKS_MUTATION_KEY = ['tasks'] as const;

type AnyMutation = Mutation<unknown, Error, unknown, unknown>;

function touchesTask(mutation: AnyMutation, taskId: number): boolean {
  const scope = mutation.options.mutationKey?.[0];
  const variables = mutation.state.variables as { taskId?: number; taskIds?: number[] } | undefined;

  if (scope === TASK_MUTATION_KEY[0]) {
    return variables?.taskId === taskId;
  }
  if (scope === TASKS_MUTATION_KEY[0]) {
    return variables?.taskIds?.includes(taskId) ?? false;
  }
  return false;
}

/** True while any keyed write for `taskId` is pending. */
export function isTaskMutating(queryClient: QueryClient, taskId: number): boolean {
  return queryClient.isMutating({ predicate: (mutation) => touchesTask(mutation, taskId) }) > 0;
}

/** True while any of `taskIds` has a pending write. */
export function isAnyTaskMutating(queryClient: QueryClient, taskIds: number[]): boolean {
  return taskIds.some((taskId) => isTaskMutating(queryClient, taskId));
}

/**
 * Run `callback` once no pending write matches `isPending`. Fires synchronously when
 * nothing is in flight. Returns an unsubscribe for the caller's cleanup — the callback
 * runs at most once either way.
 */
function whenSettled(queryClient: QueryClient, isPending: () => boolean, callback: () => void): () => void {
  if (!isPending()) {
    callback();
    return () => undefined;
  }

  let done = false;
  const unsubscribe = queryClient.getMutationCache().subscribe(() => {
    if (done || isPending()) {
      return;
    }
    done = true;
    unsubscribe();
    callback();
  });

  return () => {
    done = true;
    unsubscribe();
  };
}

/** Run `callback` once every pending write for `taskIds` has settled. */
export function whenTaskMutationsSettle(queryClient: QueryClient, taskIds: number[], callback: () => void): () => void {
  return whenSettled(queryClient, () => isAnyTaskMutating(queryClient, taskIds), callback);
}

/** Run `callback` once every pending mutation of any kind has settled. */
export function whenAllMutationsSettle(queryClient: QueryClient, callback: () => void): () => void {
  return whenSettled(queryClient, () => queryClient.isMutating() > 0, callback);
}
